import classNames from "classnames"
import { CSSProperties, useEffect, useState } from "react"
import { useSelector } from "react-redux"

import { ISupportRequest, SupporterRole, UserRole } from "@api/schema"
import ErrorPage from "@components/ErrorPage"
import { SUPPORT_REQUEST_CARD_STYLE } from "@customer-config"
import { AppState } from "@redux/reducer"
import { selectHasRole } from "@redux/reducer/auth"
import { isProjectEditor } from "@redux/reducer/myProjects"
import { useCurrentProcess } from "@services/pageHelpers"
import { StyleHelper } from "@services/styleHelper"
import { asUriFragment, iriAsHtmlId } from "@services/util"

import FlippingElement from "../../common/FlippingElement"
import SupportRequestBackCard from "./SupportRequestBackCard"
import SupportRequestFrontCard from "./SupportRequestFrontCard"


export const SUPPORT_REQUEST_DEFAULT_CLASS = "support-request-card--default"
export const SUPPORT_REQUEST_LIGHT_CLASS = "support-request-card--light"

interface IProps {
  supportRequest: ISupportRequest
  withLinkToProject?: boolean
  withLinkToSupportOffers?: boolean
  withSupportOfferCreateButton?: boolean
  editable?: boolean
}

/**
 * This component provides a single card to show a SupportRequest.
 * It uses FlippingElement to flip front and back card, that needs some special treatment -> see FlippingElement.tsx
 */
const SupportRequestCard: React.FC<IProps> = (props: IProps) => {
  const {
    editable = true,
    supportRequest,
    withLinkToProject = true,
    withLinkToSupportOffers = false,
    withSupportOfferCreateButton = false,
  } = props

  const [updateModeIsActive, setUpdateModeIsActive] = useState(false)

  // needed for currentProcess.maxPublicDaysForSupportRequest
  const { currentProcess, processRequest } = useCurrentProcess()

  /**
   * for customizing the SupportRequestCard
   * as string and not as enum or other imported variable,
   * b/c: ReferenceError: Cannot access 'SUPPORT_REQUEST_CARD_PROJEKTFABRIK_STYLE' before initialization
   *
   * @todo settings for processmanager: https://futureprojects.atlassian.net/browse/FCP-1329
   */
  const supportRequestCardStyle: string = SUPPORT_REQUEST_CARD_STYLE === "SUPPORT_REQUEST_CARD_PROJEKTFABRIK_STYLE"
    ? SUPPORT_REQUEST_DEFAULT_CLASS
    : SUPPORT_REQUEST_LIGHT_CLASS

  const isProcessmanager = useSelector((state: AppState) => selectHasRole(state, UserRole.ProcessManager))
  // special case: Processmanager not has the right to create/edit SupportRequest
  const hasEditorRights = useSelector((state: AppState) => isProjectEditor(state, supportRequest?.project?.id) && !isProcessmanager)

  const cardId = iriAsHtmlId(supportRequest)

  /** flag to be set if the component has mounted to jump to the card if its id is used as hash in the URL */
  let isMounted = false

  useEffect(() => {
    isMounted = true

    // set isMounted to false when component is unmounted
    return () => {
      isMounted = false
    }
  }, [])

  useEffect(() => {
    if (isMounted) {
      if (location.hash === asUriFragment(cardId)) {
        // setting the href lets the browser jump to the card
        location.href = asUriFragment(cardId)
      }
    }
  }, [isMounted])

  // @todo: rework: the SupportRequestCard should NOT return an error page!
  // if process could not be loaded and an error returned, display the error
  // https://futureprojects.atlassian.net/browse/FCP-1057
  if (processRequest?.loadingError) {
    return <ErrorPage statusCode={404} error={processRequest?.loadingError} />
  }

  // if the process could not be loaded, no error returned, but null returned, display a message like no process on the platform
  if (processRequest?.loaded && !currentProcess) {
    return <ErrorPage statusCode={404} error={"validate.process.noProcessCouldBeFound"} />
  }


  /**
   * make sure, publicUntil is a Date (and not string)
   */
  const publicUntil: Date = new Date(supportRequest.publicUntil)

  const finalClassName = classNames(
    supportRequestCardStyle,
    { "support-request-card--not-public": publicUntil < new Date() }
  )


  // make some styling of the card color, depending of the index of the SupporterRole in the SupporterRole enum
  const categoryCount = Object.values(SupporterRole).length
  const categoryIndex = Object.values(SupporterRole).indexOf(supportRequest.supporterRole)
  // set the background-color opacity: spreaded between all existing SupporterRoles, so that
  // every role gets its own color
  const backgroundColorOpacity: CSSProperties = StyleHelper.backgroundColorOpacity(categoryIndex, categoryCount)

  return <FlippingElement
    shouldFlip={editable && hasEditorRights && !updateModeIsActive}
    id={cardId}
  >
    <SupportRequestFrontCard
      className={finalClassName}
      maxPublicDaysForSupportRequest={currentProcess?.maxPublicDaysForSupportRequest || 0}
      setUpdateModeIsActive={setUpdateModeIsActive}
      style={backgroundColorOpacity}
      supportRequest={supportRequest}
      updateMode={updateModeIsActive}
      withLinkToProject={withLinkToProject}
      withLinkToSupportOffers={withLinkToSupportOffers}
      withSupportOfferCreateButton={withSupportOfferCreateButton}
    />
    <SupportRequestBackCard
      style={backgroundColorOpacity}
      className={finalClassName}
      setUpdateModeIsActive={setUpdateModeIsActive}
      supportRequest={supportRequest}
    />
  </FlippingElement>
}

export default SupportRequestCard
